import nanoid from 'nanoid';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { NO_DURATION_DEFINED } from '../../../../helpers/axiosWrapper/cache';
import { setPreDestinationsSelector, useStore } from '../hooks/useIMCState';
import { tagsFromTravelerInterest } from '../../_commons/fares';
import {
  setSelectedOrigin,
  setChangedOrigin,
  getSelectedOrigin
} from '../../InteractiveMapContainer/hooks/useIMCState';

export type AirportType = 'ORIGIN' | 'DESTINATION';
export type Coordinates = [latitude: number, longitude: number];

interface AirportRawData {
  category: boolean;
  label: string;
  search: string;
  value: string;
  geo?: {
    longitude?: number;
    latitude?: number;
  };
}

export interface Airport {
  category: boolean;
  label: string;
  search: string;
  value: string;
  location: Coordinates;
}

export type FetchAirports = (...args: any) => Promise<AirportRawData[]>;

type AirportOptions = {
  airportType?: AirportType;
  selectedOrigin?: string;
  cacheDuration?: number;
  cache?: boolean;
  getAirports: FetchAirports;
  meta: any;
  pageContext: any;
  initialData?: Airport[];
  integrationSettings: any;
  prePopulation: any;
  travelerInterest?: $TSFixMe;
  showInitialData?: boolean;
};

type DestinationLogic = {
  newPrePopulationAirportSetting: $TSFixMe;
  airportType: String;
};

// https://everymundo.atlassian.net/browse/FC-3609
const destinationAirportLogic = ({
  newPrePopulationAirportSetting,
  airportType
}: DestinationLogic) => {
  let airportSourceDestinations = 'TRFX';
  let routeSourceDestinations = 'TRFX';
  if (Boolean(newPrePopulationAirportSetting?.airportSourceDestinations)) {
    airportSourceDestinations = newPrePopulationAirportSetting['airportSourceDestinations'];
    delete newPrePopulationAirportSetting['airportSourceDestinations'];
  }
  if (Boolean(newPrePopulationAirportSetting?.routeSourceDestinations)) {
    routeSourceDestinations = newPrePopulationAirportSetting['routeSourceDestinations'];
    delete newPrePopulationAirportSetting['routeSourceDestinations'];
  }

  return {
    airportSource:
      airportType === 'DESTINATION'
        ? airportSourceDestinations
        : newPrePopulationAirportSetting?.airportSource,
    routeSource:
      airportType === 'DESTINATION'
        ? routeSourceDestinations
        : newPrePopulationAirportSetting?.routeSource
  };
};

const useLocations = ({
  getAirports,
  prePopulation,
  integrationSettings,
  meta,
  pageContext,
  initialData,
  airportType = 'ORIGIN',
  cache = false,
  cacheDuration = NO_DURATION_DEFINED,
  selectedOrigin,
  travelerInterest = {},
  showInitialData = false
}: AirportOptions) => {
  const setSelectedOriginFn = useStore(setSelectedOrigin);
  const setChangedOriginFn = useStore(setChangedOrigin);
  const getSelectedOriginFn = useStore(getSelectedOrigin);
  const [id] = useState(() => nanoid());
  const setPreDestinations = useStore(setPreDestinationsSelector);
  const tags = tagsFromTravelerInterest(travelerInterest);
  const newPrePopulationAirportSetting = { ...prePopulation.airport.setting };
  const filterFields =
    prePopulation.airport?.filterOriginAirportsByMarket && airportType === 'ORIGIN'
      ? prePopulation.airport.filterFields
      : undefined;

  const destinationAirportLogicParams = destinationAirportLogic({
    newPrePopulationAirportSetting,
    airportType
  });

  const query = useQuery<Airport[]>(
    [id, airportType, selectedOrigin, tags],
    () =>
      getAirports(
        {
          ...Object.assign(
            {},
            {
              ...prePopulation.airport,
              airlineIataCode: prePopulation.airlineIataCode,
              language: prePopulation.languageCode,
              routeOption: {
                airportType,
                ...(selectedOrigin && { selectedOrigin })
              }
            },
            {
              filterFields:
                tags.length > 0
                  ? [
                      {
                        name: 'tagId',
                        values: tags
                      }
                    ]
                  : filterFields,
              setting: {
                ...newPrePopulationAirportSetting,
                ...destinationAirportLogicParams
              }
            }
          )
        },
        integrationSettings,
        meta,
        pageContext,
        cache,
        cacheDuration
      ).then((data) => {
        if (
          prePopulation.origin !== undefined &&
          prePopulation.origin !== '' &&
          getSelectedOriginFn === undefined
        ) {
          for (let i = 0; i < data.length; i++) {
            if (data[i].value === prePopulation.origin) {
              setSelectedOriginFn(data[i]);
              setChangedOriginFn(true);
            }
          }
        }
        return data
          .filter((airport) => airport.geo && airport.geo.latitude && airport.geo.longitude)
          .map(({ geo, ...airport }) => ({
            ...airport,
            location: [geo?.latitude!!, geo?.longitude!!]
          }));
      }),
    {
      initialData,
      refetchOnWindowFocus: false,
      retry: 3,
      staleTime: 86400000,
      enabled: showInitialData ? true : airportType === 'ORIGIN' || !!selectedOrigin
    }
  );

  //TODO: Moving setData out of react-query's onSettled function, it is not executed when the query was cached
  if (airportType === 'DESTINATION') {
    setPreDestinations(query?.data as $TSFixMe[]);
  }

  return query;
};

export default useLocations;
